import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useBreakpointValue, ResponsiveValue } from '@chakra-ui/react';
import { ResearchShellConfig } from '../../researchShellConfig';
import { ReportingSidebarController } from './reportingSidebarInterface';
import {
    ReportingSidebarViewProps,
    ReportingSidebarControllerProps,
} from './reportingSidebarProps';
import { SidebarTreeNode } from './reportingSidebarModel';
import { TreeItem, TreeItemViewProps } from '../../view/tree';
import { HiOutlineDocumentText, HiOutlineHome, HiOutlineStar } from 'react-icons/hi';
import { createUseResizableHook } from '../../util/use-resizable';
import { GoWorkflow } from 'react-icons/go';

export function createReportingSidebarController(
    config: ResearchShellConfig
): ReportingSidebarController {
    function buildTreeItem(item: SidebarTreeNode): TreeItem {
        return {
            id: item.id,
            label: item.title,
            path: `/reporting/reports/${item.id}`,
            icon: item.icon
                ? item.icon
                : {
                      kind: 'icon',
                      Component: HiOutlineDocumentText,
                  },
            children: item.children?.map(buildTreeItem) ?? [],
            content: item.content,
        };
    }

    const useResizable = createUseResizableHook();

    return {
        useProps(context, data, props): Omit<ReportingSidebarViewProps, 'children'> {
            const {
                minWidth: minWidthProp,
                maxWidth: maxWidthProp,
                initialWidth: initialWidthProp,
            } = props;

            const resizableProps = useResizable({
                width: props.width,
                minWidth: minWidthProp,
                maxWidth: maxWidthProp,
                initialWidth: initialWidthProp,
            });

            return {
                layout: resizableProps.layout,
                resize: resizableProps.resize,
                favorites: null,
                navigation: {
                    items: props.navigation.items,
                },
                folders: [
                    {
                        id: 'workspace',
                        label: 'Workspace',
                        items: data.workspace.map((item) => ({
                            item: {
                                id: item.id,
                                label: item.title,
                                icon: item.icon,
                                path: `/reporting/reports/${item.id}`,
                                children: item.children?.map(buildTreeItem) ?? [],
                                content: item.content,
                            },
                        })),
                    },
                    {
                        id: 'draft',
                        label: 'Draft',
                        items: data.draft.map((item) => ({
                            item: {
                                id: item.id,
                                label: item.title,
                                icon: item.icon,
                                path: `/reporting/reports/${item.id}`,
                                children: item.children?.map(buildTreeItem) ?? [],
                                content: item.content,
                            },
                        })),
                    },
                ].filter((folder) => folder.items.length > 0),
                actions: props.actions,
            };
        },
    };
}
